import type { StreamingBlock } from "@storytell/streaming";
import { PmNodeType } from "@storytell/types/richText";
import { TbFileCode, TbFileTextAi, TbPhotoAi } from "solid-icons/tb";
import { Show, createMemo, onMount } from "solid-js";
import { useAppUI } from "~/components/layouts/minimal";
import { ExtendedPaneMode } from "~/components/layouts/minimal/AppUIContext";
import { GradientBorder } from "~/components/prompt/internal/components";
import { GradientBorderStatic } from "~/components/prompt/internal/components/GradientBorder";
interface ArtifactViewerRendererProps {
  block: StreamingBlock;
  onBlockClick?: () => void;
}

/**
 * Renders artifact blocks - files, code, images, or other generated content
 * that users can view in detail in the extended pane.
 */
export const ArtifactViewerRenderer = (props: ArtifactViewerRendererProps) => {
  const { state, actions } = useAppUI();
  const content = () =>
    props.block.deltas.map((delta) => delta.text).join("") ||
    (props.block.finalContent?.type === "text" ? props.block.finalContent.text : "");

  const artifactType = createMemo(() => {
    const text = content().toLowerCase();
    const title = props.block.title?.toLowerCase() || "";
    const activity = props.block.activity?.toLowerCase() || "";

    const allContent = `${text} ${title} ${activity}`;

    if (allContent.includes("code") || allContent.includes("script") || allContent.includes("function")) {
      return "code";
    }
    if (allContent.includes("image") || allContent.includes("picture") || allContent.includes("photo")) {
      return "image";
    }
    if (allContent.includes("document") || allContent.includes("file") || allContent.includes("pdf")) {
      return "document";
    }
    return "file";
  });

  const getArtifactIcon = () => {
    switch (artifactType()) {
      case "code":
        return <TbFileCode size={24} class="text-blue-600 dark:text-blue-400" />;
      case "image":
        return <TbPhotoAi size={24} class="text-blue-600 dark:text-blue-400" />;
      case "document":
        return <TbFileTextAi size={24} class="text-blue-600 dark:text-blue-400" />;
      default:
        return <TbFileTextAi size={24} class="text-blue-600 dark:text-blue-400" />;
    }
  };

  const previewText = createMemo(() => {
    const text = content().trim();
    if (!text) return "";
    return text.length > 150 ? `${text.substring(0, 150)}...` : text;
  });

  const title = createMemo(() => {
    const fallback = props.block.title;
    const finalContent = props.block.finalContent;
    if (finalContent?.type === "richText") {
      const heading = finalContent.richText.content
        .find((node) => node.type === PmNodeType.Heading)
        ?.content.map((node) => (node.type === PmNodeType.Text ? node.text : null))
        .filter(Boolean)
        .join("");
      return heading || fallback;
    }

    return fallback;
  });

  onMount(() => {
    if (!props.block.isComplete) {
      actions.setExtendedPaneArtifact(props.block.operationId, title(), props.block.blockId);
      actions.setExtendedPaneMode(ExtendedPaneMode.ACTIVE);
    }
  });

  return (
    <div class="relative">
      <GradientBorderStatic class="rounded-[0.6rem]" noGlow inset={-2} />
      <button
        type="button"
        data-renderer="artifact-viewer-renderer"
        class="relative w-full text-left bg-panel hover:bg-panel-hover border border-muted-border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-lg"
        onClick={() => {
          actions.setExtendedPaneArtifact(props.block.operationId, title(), props.block.blockId);
          actions.setExtendedPaneMode(ExtendedPaneMode.ACTIVE);
        }}
      >
        <div class="flex items-center gap-4">
          <div class="flex-shrink-0 flex items-center justify-center">{getArtifactIcon()}</div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <Show
                when={title()}
                fallback={<span class="text-base font-medium text-primary-text">Generated Artifact</span>}
              >
                <span class="text-base font-medium text-primary-text">{title()}</span>
              </Show>

              <Show when={!props.block.isComplete}>
                <div class="w-2 h-2 bg-brand rounded-full animate-pulse" />
              </Show>
            </div>

            <span class="text-xs text-secondary-text">Click to view in detail</span>
          </div>
        </div>
      </button>
    </div>
  );
};
